/**
 * src/api/connectors.js
 *
 * All API calls related to connectors.
 * Bearer token is attached automatically from the auth cookie.
 */

import { authedJson } from './http'

const apiFetchGet = path => authedJson('GET', path)

/**
 * Fetch all active connectors.
 * GET /connector/active/
 * @returns {Promise<{ connectors: ConnectorItem[] }>}
 */
export async function fetchActiveConnectors() {
  return apiFetchGet('/connector/active/')
}

/**
 * Fetch the connector catalog (available but not yet connected).
 * GET /connector/catalog
 * @returns {Promise<{ connectors: CatalogConnectorItem[] }>}
 */
export async function fetchConnectorCatalog() {
  return apiFetchGet('/connector/catalog/')
}

/**
 * Fetch configuration properties for a specific connector.
 * GET /connector/props/{connector_id}
 * @param {string|number} connectorId
 * @returns {Promise<object>}
 */
export async function fetchConnectorProps(connectorId) {
  return apiFetchGet(`/connector/props/${connectorId}`)
}

/**
 * Add a connector from the catalog.
 * POST /connector/addconnector/{catalog_id}
 * @param {string|number} catalogId
 * @returns {Promise<object>}
 */
export async function addConnectorFromCatalog(catalogId) {
  return authedJson('POST', `/connector/addconnector/${catalogId}`)
}

/**
 * Toggle the enabled state of a connector action.
 * POST /connector/actions/{action_id}/toggle
 * @param {string|number} actionId
 * @returns {Promise<object>}
 */
export async function toggleConnectorAction(actionId) {
  return authedJson('POST', `/connector/actions/${actionId}/toggle`)
}

/**
 * Update a single connector property's value.
 * POST /connector/{connector_id}/props
 * @param {string|number} connectorId
 * @param {{ action_name: string, prop_key: string, prop_value: string }} payload
 * @returns {Promise<object>}
 */
export async function updateConnectorProp(connectorId, payload) {
  return authedJson('POST', `/connector/${connectorId}/props`, payload)
}

/**
 * List every action_name/display_name pair for a connector, regardless of
 * whether the action has any configured properties or transformer yet.
 * GET /connector/{connector_id}/actions
 * @param {string|number} connectorId
 * @returns {Promise<{ actions: Array<{action_name: string, display_name: string}> }>}
 */
export async function fetchConnectorActions(connectorId) {
  return apiFetchGet(`/connector/${connectorId}/actions`)
}

/**
 * List all transformer versions for a connector action, newest first.
 * GET /connector/{connector_id}/transformers/{action_name}
 * @param {string|number} connectorId
 * @param {string} actionName
 * @returns {Promise<{ versions: Array<{transformer_id: number, version: string|null, yaml: string, created_at: string}> }>}
 */
export async function fetchTransformerVersions(connectorId, actionName) {
  return apiFetchGet(`/connector/${connectorId}/transformers/${encodeURIComponent(actionName)}`)
}

/**
 * Save a new transformer version for a connector action. Never overwrites the
 * prior version — always inserts a new row; the version number is assigned
 * server-side.
 * POST /connector/{connector_id}/transformers
 * @param {string|number} connectorId
 * @param {{ action_name: string, yaml: string }} payload
 * @returns {Promise<{ transformer: {transformer_id: number, version: string, yaml: string, created_at: string} }>}
 */
export async function createTransformerVersion(connectorId, payload) {
  return authedJson('POST', `/connector/${connectorId}/transformers`, payload)
}
